import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/App.css';
import '../styles/DeleteAccount.css';

function DeleteAccount() {
  const [formData, setFormData] = useState({
    email: '',
    reason: '',
    confirmed: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.confirmed) {
      errors.confirmed = 'You must confirm that you understand this action cannot be undone';
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSuccessMessage('');

    try {
      await addDoc(collection(db, 'deletionRequests'), {
        email: formData.email,
        reason: formData.reason || '',
        timestamp: new Date(),
        status: 'pending',
        processed: false,
      });

      console.log('✅ Deletion request submitted:', formData.email);

      setSuccessMessage('Your deletion request has been received. We will process it within 30 days.');
      setFormData({ email: '', reason: '', confirmed: false });
      setErrors({});

      setTimeout(() => {
        setSuccessMessage('');
        setIsSubmitting(false);
      }, 5000);
    } catch (error) {
      console.error('❌ Error submitting deletion request:', error);
      setErrors({ form: 'Something went wrong. Please try again or contact support.' });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="home">
      <SEO
        title="Delete Account"
        description="Request to delete your Begin Strength account and all associated data. This action cannot be undone."
        image="/social-media-image.png"
        path="/delete"
      />
      <Header />
      <main className="delete-account-page">
        <div className="delete-account-container">
          <h1 className="delete-account-title">
            Delete Your <span style={{ color: 'red' }}>Begin Strength</span> Account
          </h1>
          
          <div className="delete-account-warning">
            <h2>⚠️ Important Information</h2>
            <p>
              Requesting account deletion will permanently delete your account and all associated data. 
              This action <strong>cannot be undone</strong>.
            </p>
            <p>
              We will process your request within <strong>30 days</strong>.
            </p>
          </div>

          <div className="delete-account-what-gets-deleted">
            <h2>What Gets Deleted?</h2>
            <ul>
              <li>Your profile and personal information</li>
              <li>All workout history and progress</li>
              <li>All scheduled activities</li>
              <li>Your training program data</li>
              <li>All performance tracking data</li>
              <li>Apple Health sync data (if connected)</li>
            </ul>
            <p className="delete-account-note">
              <strong>Note:</strong> If you have an active subscription, you will need to cancel it separately 
              through your app store account. Account deletion does not automatically cancel subscriptions.
            </p>
          </div>

          <form className="delete-account-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">
                Email Address <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter the email address associated with your account"
                className={`form-input ${errors.email ? 'error' : ''}`}
                value={formData.email}
                onChange={handleChange}
                required
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="reason">Reason for Deletion (Optional)</label>
              <textarea
                id="reason"
                name="reason"
                placeholder="Help us improve by sharing why you're deleting your account (optional)"
                className="form-textarea"
                value={formData.reason}
                onChange={handleChange}
                rows="4"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="confirmed"
                  checked={formData.confirmed}
                  onChange={handleChange}
                  className="form-checkbox"
                />
                <span>
                  I understand that this action <strong>cannot be undone</strong> and all my data will be 
                  permanently deleted within 30 days.
                </span>
              </label>
              {errors.confirmed && <span className="error-message">{errors.confirmed}</span>}
            </div>

            {errors.form && <div className="error-message form-error">{errors.form}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <button
              type="submit"
              className="c-ripple delete-account-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting Request...' : 'Submit Deletion Request'}
            </button>
          </form>

          <div className="delete-account-help">
            <h3>Need Help?</h3>
            <p>
              If you're having issues with your account or have questions, please{' '}
              <a href="/support" style={{ color: 'red', textDecoration: 'none' }}>
                contact our support team
              </a>{' '}
              before requesting deletion.
            </p>
            <p>
              For more information, see our{' '}
              <a href="/faqs" style={{ color: 'red', textDecoration: 'none' }}>
                FAQ section
              </a>{' '}
              or{' '}
              <a href="/privacy" style={{ color: 'red', textDecoration: 'none' }}>
                Privacy Policy
              </a>.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default DeleteAccount;
